import React, { useState } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
  Spinner,
  Alert,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { generateOTP } from "../../../actions/otp";
import { alertError, alertSuccess } from "../../../helpers/errorHandling";

const ResetPassword = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMsg, setErrorMsg] = useState(""); // Manage error message state

  const validation = useFormik({
    initialValues: {
      email: "",
      otp: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .required("Please enter your email")
        .email("Invalid email format"),
      password: Yup.string()
        .required("Please enter your new password")
        .min(6, "Password must be at least 6 characters"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password"), null], "Passwords must match")
        .required("Please confirm your password"),
      otp: otpSent
        ? Yup.string().required("Please enter the OTP")
        : Yup.string(),
    }),
    onSubmit: async (values) => {
      setIsLoading(true);
      const { email, otp, password } = values;
      try {
        // await dispatch(resetPasswordAction({ email, otp, password }));
        setIsLoading(false);
        setSuccessMessage("Password has been reset successfully!");
        setErrorMsg(""); // Clear any previous error messages
      } catch (error) {
        setIsLoading(false);
        setErrorMsg("Failed to reset password. Please try again."); // Set error message
      }
    },
  });

  const generateOTPFunc = async () => {
    try {
      const data = {
        mobile: "557672140",
        otpType: "partnerResetPassword",
      };
      const res = await generateOTP(data);
      if (res?.status === "success") {
        alertSuccess(res?.message);
      }
      setOtpSent(true);
    } catch (e) {
      alertError({ message: e?.message });
      console.log(e);
    }
  };

  return (
    <Container className="d-flex align-items-center justify-content-center">
      <Row className="justify-content-center" style={{ marginTop: 100 }}>
        <Col md="6">
          <div className="text-center">
            <h4>Reset Your Password</h4>
            <p>Please enter your email and new password.</p>
          </div>
          {successMessage && <Alert color="success">{successMessage}</Alert>}
          {errorMsg && <Alert color="danger">{errorMsg}</Alert>}{" "}
          {/* Display error message once */}
          <Card>
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                validation.handleSubmit();
              }}
            >
              <div className="p-4">
                <Row>
                  <Col md={12} className="mb-3">
                    <Label className="form-label">Email</Label>
                    <Input
                      autoComplete="off"
                      name="email"
                      className="form-control"
                      placeholder="Enter your email"
                      type="email"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.email || ""}
                      invalid={
                        validation.touched.email && validation.errors.email
                          ? true
                          : false
                      }
                    />
                    {validation.touched.email && validation.errors.email && (
                      <FormFeedback type="invalid">
                        {validation.errors.email}
                      </FormFeedback>
                    )}
                  </Col>

                  <Col md={12} className="mb-3">
                    <Label className="form-label">New Password</Label>
                    <Input
                      name="password"
                      type="password"
                      className="form-control"
                      placeholder="Enter new password"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.password || ""}
                      invalid={
                        validation.touched.password &&
                        validation.errors.password
                          ? true
                          : false
                      }
                    />
                    {validation.touched.password &&
                      validation.errors.password && (
                        <FormFeedback type="invalid">
                          {validation.errors.password}
                        </FormFeedback>
                      )}
                  </Col>

                  <Col md={12} className="mb-3">
                    <Label className="form-label">Confirm Password</Label>
                    <Input
                      name="confirmPassword"
                      type="password"
                      className="form-control"
                      placeholder="Confirm new password"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.confirmPassword || ""}
                      invalid={
                        validation.touched.confirmPassword &&
                        validation.errors.confirmPassword
                          ? true
                          : false
                      }
                    />
                    {validation.touched.confirmPassword &&
                      validation.errors.confirmPassword && (
                        <FormFeedback type="invalid">
                          {validation.errors.confirmPassword}
                        </FormFeedback>
                      )}
                  </Col>

                  {/* OTP Section: OTP field with Get OTP button to the right */}
                  <Col md={12} className="mb-3 d-flex">
                    <div className="w-75">
                      <Label className="form-label">Enter OTP</Label>
                      <Input
                        name="otp"
                        className="form-control"
                        placeholder="Enter the OTP sent to your email"
                        type="text"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.otp || ""}
                        invalid={
                          validation.touched.otp && validation.errors.otp
                            ? true
                            : false
                        }
                      />
                      {validation.touched.otp && validation.errors.otp && (
                        <FormFeedback type="invalid">
                          {validation.errors.otp}
                        </FormFeedback>
                      )}
                    </div>
                    <Button
                      color="secondary"
                      onClick={() => generateOTPFunc()}
                      disabled={isLoading || otpSent}
                      className={`ms-3 align-self-end ${
                        validation.touched.otp && validation.errors.otp
                          ? "mb-4"
                          : ""
                      }`}
                    >
                      {isLoading ? (
                        <Spinner size="sm" className="me-2" />
                      ) : null}
                      {otpSent ? "OTP Sent" : "Get OTP"}
                    </Button>
                  </Col>

                  <Col md={12} className="mb-3">
                    <Button
                      size="md"
                      color="primary"
                      className="w-100"
                      type="submit"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <Spinner size="sm" className="me-2">
                          Loading...
                        </Spinner>
                      ) : null}
                      Reset Password
                    </Button>
                  </Col>
                </Row>
              </div>
            </Form>
          </Card>
          <div className="d-flex flex-column justify-content-center align-items-center gap-2 fs-13">
            <Link className="text-info text-decoration-underline" to="/login">
              Go back to Login, Click here
            </Link>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default ResetPassword;
